import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { People, Phishing, Scale, Speed, FilterAltOff } from "@mui/icons-material";
import { Tooltip as MuiTooltip } from "@mui/material";
import { formatarKg, formatarNumero, formatarRendimento } from '../../utils/formatters';
import { /* PieChart, Pie, */ Cell, LabelList, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip /* , Legend, CartesianGrid, Line, LineChart */ } from 'recharts';
import styles from '../../assets/styles/LayoutShared.module.css';
import MapaProducao from './MapaProducao';
import CartaoExpansivel from '../../components/Dashboard/CartaoExpansivel';
import FiltroLocalArvore, { NoLocalArvore } from '../../components/Dashboard/FiltroLocalArvore';
import EvolucaoProducao from '../../components/Dashboard/EvolucaoProducao';
import EsforcoPesca from '../../components/Dashboard/EsforcoPesca';
import AtividadeInterrompida from '../../components/Dashboard/AtividadeInterrompida';
import QualidadeDados from '../../components/Dashboard/QualidadeDados';
import AvisoDadosDesatualizados from '../../components/Dashboard/AvisoDadosDesatualizados';
import { devePiracema } from '../../utils/piracema';
import { montarUrlRegistrosSemLocal } from '../../utils/registrosPendencias';
import FiltroProjetosMulti from '../../components/Dashboard/FiltroProjetosMulti';
import { paramProjetos } from '../../utils/filtroProjetos';

// 1. Defina o tipo fora do componente para organizar
interface FiltrosState {
  // Espécie: o filtro é a IDENTIDADE (uid_pesca_especie); o nome é só rótulo (chip/título do gráfico).
  uid_pesca_especie: string | null;
  nome_especie: string | null;
  local: string | null;
  ambiente: string | null;
  dataInicio: string;
  dataFim: string;
  // Projetos selecionados (vários). Lista vazia = todos / sem filtro de projeto.
  uid_pesca_projetos: string[];
  uid_pesca_area: string | null;
  uid_pesca_pescador: string | null;
  uid_pesca_colonia: string | null;
  // Validação: "todos" (padrão) ou só os registros já validados (data de validação preenchida)
  validacao: 'todos' | 'somente_validados';
  // Árvore geográfica canônica (local estruturado) — diferente de `local`
  // acima, que é o texto livre legado. Lista de nós (cada um vale pela
  // subárvore inteira). Vem do filtro "Local de pesca", da navegação do
  // gráfico "Produção por local" e do clique no mapa; os demais widgets
  // leem daqui porque todos compartilham este mesmo estado `filtros`.
  uid_pesca_local: string[];
  // Petrecho (apetrecho): filtro N:N no backend — vem do clique numa barra de "Esforço de pesca".
  uid_pesca_apetrecho: string | null;
}

const formatarDataLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 0 = segunda ... 6 = domingo
const diasDesdeSegunda = (data: Date) => (data.getDay() + 6) % 7;

const getIntervaloDatas = (opcao: string) => {
  // Data de referência do sistema: 15/03/2026 (Domingo)
  const hoje = new Date();
  let inicio = new Date(hoje);
  let fim = new Date(hoje);

  switch (opcao) {
    case 'Hoje':
      break;
    case 'Ontem':
      inicio.setDate(hoje.getDate() - 1);
      fim.setDate(hoje.getDate() - 1);
      break;
    // Semana de segunda a domingo (a mesma da agregação "Semana" da Evolução da produção).
    case 'Essa semana':
      inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - diasDesdeSegunda(hoje));
      fim = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + 6);
      break;
    case 'Semana passada':
      inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - diasDesdeSegunda(hoje) - 7);
      fim = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + 6);
      break;
    case 'Esse Mes':
      inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      break;
    case 'Mes passado':
      inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
      fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0);
      break;
    case 'Esse ano':
      inicio = new Date(hoje.getFullYear(), 0, 1);
      fim = new Date(hoje.getFullYear(), 11, 31);
      break;
  }

  return {
    inicio: formatarDataLocal(inicio),
    fim: formatarDataLocal(fim)
  };
};



const DEBOUNCE_DATAS_MS = 500;
const ANO_MINIMO = 1900;
const ANO_MAXIMO = 2100;

// Valida o par digitado: só aceita aaaa-mm-dd (ano de 4 dígitos), data existente e início <= fim.
// Os dois vazios = sem filtro de período.
const validarPeriodoDigitado = (inicio: string, fim: string): { valido: boolean; aviso: string | null } => {
  if (!inicio && !fim) return { valido: true, aviso: null };
  if (!inicio || !fim) return { valido: false, aviso: 'Preencha as duas datas para filtrar o período.' };
  const datas = [inicio, fim].map((d) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (!m) return false;
    const [a, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (a < ANO_MINIMO || a > ANO_MAXIMO) return false;
    const dt = new Date(Date.UTC(a, mes - 1, dia));
    return dt.getUTCFullYear() === a && dt.getUTCMonth() === mes - 1 && dt.getUTCDate() === dia;
  });
  if (!datas[0] || !datas[1]) return { valido: false, aviso: `Informe datas válidas (ano entre ${ANO_MINIMO} e ${ANO_MAXIMO}).` };
  if (inicio > fim) return { valido: false, aviso: 'A data inicial não pode ser depois da data final.' };
  return { valido: true, aviso: null };
};

// Rótulo do eixo "Produção por local": até 2 linhas, sem cortar nomes curtos; o nome completo fica no <title>.
const quebrarRotulo = (texto: string, max = 26): string[] => {
  if (texto.length <= max) return [texto];
  const palavras = texto.split(' ');
  let linha1 = '';
  let i = 0;
  while (i < palavras.length && (linha1 ? linha1.length + 1 + palavras[i].length : palavras[i].length) <= max) {
    linha1 = linha1 ? `${linha1} ${palavras[i]}` : palavras[i];
    i++;
  }
  let resto = palavras.slice(i).join(' ');
  if (!linha1) { linha1 = texto.slice(0, max); resto = texto.slice(max); }
  return [linha1, resto.length > max ? `${resto.slice(0, max - 1)}…` : resto];
};

const RotuloEixoLocal = ({ x, y, payload }: any) => {
  const linhas = quebrarRotulo(String(payload.value));
  return (
    <text x={x} y={y} textAnchor="end" fontSize={12} fontWeight="bold" fill="#666">
      <title>{payload.value}</title>
      {linhas.map((l, i) => (
        <tspan key={i} x={x} dy={i === 0 ? (linhas.length > 1 ? '-0.25em' : '0.35em') : '1.15em'}>{l}</tspan>
      ))}
    </text>
  );
};

const TOP_ESPECIES = 10;
const TOP_LOCAIS = 10;

// Mesmos parâmetros que /pesca/stats e /pesca/stats/producao-por-local entendem.
// `incluirLocal=false` omite uid_pesca_local: o gráfico "Produção por local" usa esse campo
// como uid_pai (nível atual), não como filtro — senão a raiz viraria só os ancestrais do nó.
const montarQueryFiltros = (filtros: FiltrosState, incluirLocal = true): URLSearchParams => {
  const query = new URLSearchParams();
  if (filtros.uid_pesca_especie) query.append('uid_pesca_especie', filtros.uid_pesca_especie);
  if (filtros.local) query.append('local', filtros.local);
  if (filtros.ambiente) query.append('ambiente', filtros.ambiente);
  if (filtros.dataInicio) query.append('dataInicio', filtros.dataInicio);
  if (filtros.dataFim) query.append('dataFim', filtros.dataFim);
  const projetos = paramProjetos(filtros.uid_pesca_projetos);
  if (projetos) query.append('uid_pesca_projetos', projetos);
  if (filtros.uid_pesca_area) query.append('uid_pesca_area', filtros.uid_pesca_area);
  if (filtros.uid_pesca_pescador) query.append('uid_pesca_pescador', filtros.uid_pesca_pescador);
  if (filtros.uid_pesca_colonia) query.append('uid_pesca_colonia', filtros.uid_pesca_colonia);
  if (filtros.validacao === 'somente_validados') query.append('validacao', filtros.validacao);
  if (filtros.uid_pesca_apetrecho) query.append('uid_pesca_apetrecho', filtros.uid_pesca_apetrecho);
  if (incluirLocal && filtros.uid_pesca_local.length) query.append('uid_pesca_local', filtros.uid_pesca_local.join(','));
  return query;
};

interface ItemProducaoLocal {
  uid_pesca_local: string | null;
  nome: string;
  tipo: string | null;
  kg: number;
  registros: number;
  temFilhos: boolean;
  categoria: 'no' | 'proprio' | 'sem_vinculo';
}

interface NivelProducaoLocal {
  uid_pai: string | null;
  caminho: { uid_pesca_local: string; nome: string }[];
  totalKg: number;
  itens: ItemProducaoLocal[];
}

const formatarPercentual = (valor: number, total: number): string => {
  if (!(total > 0)) return 'n/d';
  const pct = (valor / total) * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${formatarNumero(pct, 0)}%`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // const COLORS = ['#00c3ff', '#00b37e', '#ffbb28', '#f75a68', '#92989b', '#6b5ce7', '#ff8042'];

  const [periodoSelecionado, setPeriodoSelecionado] = useState('Esse ano');

  const [filtros, setFiltros] = useState<FiltrosState>(() => {
    const { inicio, fim } = getIntervaloDatas('Esse ano');
    return {
      uid_pesca_especie: null,
      nome_especie: null,
      local: null,
      ambiente: null,
      dataInicio: inicio,
      dataFim: fim,
      uid_pesca_projetos: [],
      uid_pesca_area: null,
      uid_pesca_pescador: null,
      uid_pesca_colonia: null,
      validacao: 'todos',
      uid_pesca_local: [],
      uid_pesca_apetrecho: null,
    };
  });

  // Opções do seletor no topo (Projeto/Área/Colônia) — os endpoints já vêm
  // restritos ao(s) projeto(s) do usuário logado (getProjetosPermitidos no
  // backend, Epic 1), não precisa de nenhuma lógica extra aqui pra esconder
  // opção. Colônia não é filtrada por projeto no backend, mas isso é seguro:
  // a permissão real é sempre aplicada por cima, no filtro de projeto do
  // /pesca/stats (ver controller.ts).
  const [projetos, setProjetos] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [colonias, setColonias] = useState<any[]>([]);

  useEffect(() => {
    // Listas dos seletores: se falhar, o seletor fica só com a opção "Todos" (sem estourar erro na tela).
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data)).catch(() => setProjetos([]));
    api.get('/pesca/areas').then(res => setAreas(res.data.data)).catch(() => setAreas([]));
    api.get('/pesca/colonias').then(res => setColonias(res.data.data)).catch(() => setColonias([]));
  }, []);

  // Busca de Pescador com autocomplete (mesmo padrão de município em
  // PescadorForm.tsx): digita, debounce, sugere, escolhe.
  const [pescadorBusca, setPescadorBusca] = useState('');
  const [pescadorSelecionado, setPescadorSelecionado] = useState<{ uid_pesca_pescador: string; nome: string } | null>(null);
  const [sugestoesPescador, setSugestoesPescador] = useState<any[]>([]);
  const [buscandoPescador, setBuscandoPescador] = useState(false);
  const pescadorDebounce = useRef<any>(null);
  const buscaPescadorId = useRef(0); // descarta resposta de busca antiga que chega depois da nova

  const handleBuscarPescador = (texto: string) => {
    setPescadorBusca(texto);
    buscaPescadorId.current++;
    setPescadorSelecionado(null);
    setFiltros(prev => ({ ...prev, uid_pesca_pescador: null }));
    if (pescadorDebounce.current) clearTimeout(pescadorDebounce.current);

    if (texto.trim().length < 2) {
      setSugestoesPescador([]);
      return;
    }

    pescadorDebounce.current = setTimeout(async () => {
      const id = ++buscaPescadorId.current;
      setBuscandoPescador(true);
      try {
        const res = await api.get(`/pesca/pescadores?search=${encodeURIComponent(texto.trim())}&limit=20`);
        if (id === buscaPescadorId.current) setSugestoesPescador(res.data.data);
      } catch {
        if (id === buscaPescadorId.current) setSugestoesPescador([]);
      } finally {
        if (id === buscaPescadorId.current) setBuscandoPescador(false);
      }
    }, 400);
  };

  const selecionarPescador = (p: any) => {
    setPescadorSelecionado({ uid_pesca_pescador: p.uid_pesca_pescador, nome: p.nome });
    setPescadorBusca(p.nome);
    setSugestoesPescador([]);
    setFiltros(prev => ({ ...prev, uid_pesca_pescador: p.uid_pesca_pescador }));
  };

  const limparPescador = () => {
    setPescadorBusca('');
    setPescadorSelecionado(null);
    setSugestoesPescador([]);
    setFiltros(prev => ({ ...prev, uid_pesca_pescador: null }));
  };

  // Datas digitadas à mão: o <input type="date"> emite valores incompletos enquanto a pessoa digita
  // (ex.: ano "0002"). Só vira filtro quando as duas datas estão completas e válidas, 500 ms depois da
  // última digitação; antes disso, aviso discreto e nenhuma busca.
  const [dataInicioDigitada, setDataInicioDigitada] = useState(filtros.dataInicio);
  const [dataFimDigitada, setDataFimDigitada] = useState(filtros.dataFim);
  const [avisoPeriodo, setAvisoPeriodo] = useState<string | null>(null);
  const [avisoBusca, setAvisoBusca] = useState<string | null>(null);
  const [tentativaStats, setTentativaStats] = useState(0); // "Tentar de novo" refaz a busca sem mudar os filtros

  useEffect(() => {
    const t = setTimeout(() => {
      const r = validarPeriodoDigitado(dataInicioDigitada, dataFimDigitada);
      setAvisoPeriodo(r.aviso);
      if (r.valido) {
        setFiltros(prev => (prev.dataInicio === dataInicioDigitada && prev.dataFim === dataFimDigitada
          ? prev
          : { ...prev, dataInicio: dataInicioDigitada, dataFim: dataFimDigitada }));
      }
    }, DEBOUNCE_DATAS_MS);
    return () => clearTimeout(t);
  }, [dataInicioDigitada, dataFimDigitada]);

  // BUSCA DADOS SEMPRE QUE UM FILTRO MUDA
  useEffect(() => {
    let ativo = true;
    setLoading(true);
    const query = montarQueryFiltros(filtros);

    api.get(`/pesca/stats?${query.toString()}`)
      .then(res => {
        if (!ativo) return;
        setStats(res.data.data);
        setAvisoBusca(null);
      })
      .catch(err => {
        // Mantém os últimos números na tela e avisa, em vez de estourar a tela de erro.
        if (ativo) setAvisoBusca(err?.response?.data?.message || 'Não foi possível atualizar os indicadores para este filtro.');
      })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [filtros, tentativaStats]);

  // "Produção por local": a navegação É o filtro de local. O nível atual do gráfico é
  // filtros.uid_pesca_local (uid_pai); descer/subir no breadcrumb troca esse filtro e o
  // Dashboard inteiro reage. O gráfico não manda esse campo como filtro (ver
  // montarQueryFiltros), só os demais filtros globais. O breadcrumb vem do `caminho` que o
  // backend devolve pro uid_pai, então vale também quando o filtro vem do clique no mapa.
  const [nivelLocal, setNivelLocal] = useState<NivelProducaoLocal | null>(null);
  const [carregandoLocal, setCarregandoLocal] = useState(false);
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const [tentativaLocal, setTentativaLocal] = useState(0);

  useEffect(() => {
    let ativo = true;
    setCarregandoLocal(true);
    const query = montarQueryFiltros(filtros, false);
    // 1 local = navega dentro dele (uid_pai); vários = compara os selecionados lado a lado.
    if (filtros.uid_pesca_local.length === 1) query.append('uid_pai', filtros.uid_pesca_local[0]);
    else if (filtros.uid_pesca_local.length > 1) query.append('comparar', filtros.uid_pesca_local.join(','));
    api.get(`/pesca/stats/producao-por-local?${query.toString()}`)
      .then((res) => { if (ativo) { setNivelLocal(res.data.data); setErroLocal(null); } })
      // mantém o nível anterior na tela (não apaga) e avisa que está desatualizado
      .catch((e) => { if (ativo) setErroLocal(e?.response?.data?.message || 'Falha ao carregar a produção por local.'); })
      .finally(() => { if (ativo) setCarregandoLocal(false); });
    return () => { ativo = false; };
  }, [filtros, tentativaLocal]);

  // Navegar substitui a seleção por UM nó (ou limpa, com null).
  const irParaLocal = (uid: string | null) => {
    setFiltros(prev => {
      const alvo = uid ? [uid] : [];
      const igual = prev.uid_pesca_local.length === alvo.length && prev.uid_pesca_local.every((u, i) => u === alvo[i]);
      return igual ? prev : { ...prev, uid_pesca_local: alvo };
    });
  };

  const modoComparacaoLocal = filtros.uid_pesca_local.length > 1;
  const caminhoLocal = filtros.uid_pesca_local.length === 1 && nivelLocal?.uid_pai === filtros.uid_pesca_local[0] ? nivelLocal.caminho : [];

  // Árvore pro filtro "Local de pesca" (endpoint de leitura, não-admin).
  // Só mostra os locais dos projetos marcados (sem projeto marcado = todos os permitidos).
  const [arvoreLocais, setArvoreLocais] = useState<NoLocalArvore[]>([]);
  const projetosParam = paramProjetos(filtros.uid_pesca_projetos);
  useEffect(() => {
    let ativo = true;
    api.get('/pesca/locais/arvore', { params: projetosParam ? { uid_pesca_projetos: projetosParam } : undefined })
      .then((res) => { if (ativo) setArvoreLocais(res.data.data || []); })
      .catch(() => { if (ativo) setArvoreLocais([]); });
    return () => { ativo = false; };
  }, [projetosParam]);

  // Locais marcados que saíram da árvore (projeto desmarcado) deixam de filtrar: senão o painel
  // ficaria vazio sem o usuário entender por quê.
  useEffect(() => {
    if (!arvoreLocais.length) return;
    const existentes = new Set(arvoreLocais.map((n) => n.uid_pesca_local));
    setFiltros((prev) => {
      const restantes = prev.uid_pesca_local.filter((u) => existentes.has(u));
      return restantes.length === prev.uid_pesca_local.length ? prev : { ...prev, uid_pesca_local: restantes };
    });
  }, [arvoreLocais]);
  const [maisFiltrosAberto, setMaisFiltrosAberto] = useState(false);
  // Nome do petrecho filtrado (só pra o chip; o filtro em si é o uid).
  const [nomePetrecho, setNomePetrecho] = useState<string | null>(null);
  const selecionarPetrecho = (uid: string | null, nome: string | null) => {
    setNomePetrecho(uid ? nome : null);
    setFiltros(prev => ({ ...prev, uid_pesca_apetrecho: uid }));
  };

  const handleSelectPeriodo = (valor: string) => {
    setPeriodoSelecionado(valor);
    const { inicio, fim } = getIntervaloDatas(valor);
    setDataInicioDigitada(inicio);
    setDataFimDigitada(fim);
    setFiltros(prev => ({
      ...prev,
      dataInicio: inicio,
      dataFim: fim
    }));
  };

  // Clique numa barra de espécie: filtra pelo uid (e guarda o nome só pra exibir); clicar de novo limpa.
  const selecionarEspecie = (uid: string, nome: string) => {
    setFiltros(prev => (prev.uid_pesca_especie === uid
      ? { ...prev, uid_pesca_especie: null, nome_especie: null }
      : { ...prev, uid_pesca_especie: uid, nome_especie: nome }));
  };

  if (!stats) {
    if (!loading && avisoBusca) {
      return (
        <div style={{ padding: '20px' }}>
          <AvisoDadosDesatualizados erro comDadosAnteriores={false} detalhe={avisoBusca} onTentarDeNovo={() => setTentativaStats((t) => t + 1)} />
        </div>
      );
    }
    return <p>Carregando indicadores...</p>;
  }

  // PREPARAÇÃO DE DADOS


  // Top 10 espécies + "Outras" (soma do restante). O percentual é calculado
  // sobre o total do gráfico (todas as espécies, antes do corte).
  const especiesOrdenadas = (stats?.charts?.especies || [])
    .map((item: any) => ({
      uid: item.uid_pesca_especie as string,
      label: item.label,
      value: parseFloat(item.value) || 0
    }))
    .sort((a: any, b: any) => b.value - a.value);
  const totalEspecies = especiesOrdenadas.reduce((acc: number, e: any) => acc + e.value, 0);
  const excedentes = especiesOrdenadas.slice(TOP_ESPECIES);
  const restoEspecies = excedentes.reduce((acc: number, e: any) => acc + e.value, 0);
  // No modo expandido (tela cheia) não agrupa: mostra todas as espécies.
  const montarDadosEspecies = (agrupar: boolean) => (agrupar && excedentes.length > 0
    ? [
        ...especiesOrdenadas.slice(0, TOP_ESPECIES),
        { label: `Outras (${excedentes.length} ${excedentes.length === 1 ? 'espécie' : 'espécies'})`, value: restoEspecies, agrupada: true }
      ]
    : especiesOrdenadas
  ).map((e: any) => ({
    ...e,
    rotuloBarra: `${formatarKg(e.value)} kg · ${formatarPercentual(e.value, totalEspecies)}`
  }));

  // Top 10 + "Outros (N locais)" no cartão normal; tela cheia mostra todos.
  const montarDadosLocais = (agrupar: boolean) => {
    const itens: ItemProducaoLocal[] = nivelLocal?.itens || [];
    const totalNivel = nivelLocal?.totalKg || 0;
    const nomes = itens.map((i) => i.nome);
    const rotulo = (i: ItemProducaoLocal) => (nomes.filter((n) => n === i.nome).length > 1 && i.tipo ? `${i.nome} (${i.tipo})` : i.nome);
    const base: any[] = itens.map((i) => ({
      label: rotulo(i), value: i.kg, uid: i.uid_pesca_local, categoria: i.categoria, temFilhos: i.temFilhos, agrupada: false,
    }));
    const lista = agrupar && base.length > TOP_LOCAIS
      ? [
          ...base.slice(0, TOP_LOCAIS),
          { label: `Outros (${base.length - TOP_LOCAIS} locais)`, value: base.slice(TOP_LOCAIS).reduce((a, i) => a + i.value, 0), uid: null, categoria: 'agrupado', temFilhos: false, agrupada: true },
        ]
      : base;
    return lista.map((e) => ({ ...e, rotuloBarra: `${formatarKg(e.value)} kg · ${formatarPercentual(e.value, totalNivel)}` }));
  };


  const qtdFiltrosSeletorAtivos = [filtros.uid_pesca_projetos.length > 0, filtros.uid_pesca_area, filtros.uid_pesca_pescador, filtros.uid_pesca_colonia, filtros.uid_pesca_local.length > 0, filtros.uid_pesca_apetrecho, filtros.validacao === 'somente_validados'].filter(Boolean).length;

  // Título do mapa: nome do local selecionado (ou "N locais" na comparação).
  const tituloLocalMapa = filtros.uid_pesca_local.length === 0
    ? null
    : modoComparacaoLocal
      ? `${filtros.uid_pesca_local.length} locais`
      : (caminhoLocal.length ? caminhoLocal[caminhoLocal.length - 1].nome : (arvoreLocais.find((n) => n.uid_pesca_local === filtros.uid_pesca_local[0])?.nome || null));

  const limparTudo = () => {
    setFiltros({
      uid_pesca_especie: null, nome_especie: null, local: null, ambiente: null, dataInicio: '', dataFim: '',
      uid_pesca_projetos: [], uid_pesca_area: null, uid_pesca_pescador: null, uid_pesca_colonia: null, validacao: 'todos',
      uid_pesca_local: [],
      uid_pesca_apetrecho: null,
    });
    setNomePetrecho(null);
    limparPescador();
    setDataInicioDigitada('');
    setDataFimDigitada('');
  };

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h1 className={styles.greenTitle}>Análise de Monitoramento Pesqueiro</h1>

        {/* BOTÃO PARA LIMPAR FILTROS */}
        {(filtros.uid_pesca_especie || filtros.local || filtros.ambiente || qtdFiltrosSeletorAtivos > 0) && (
          <button
            onClick={limparTudo}
            style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#f75a68', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            <FilterAltOff fontSize="small" /> Limpar Filtros
          </button>
        )}
      </div>

      {/* SELETOR NO TOPO DO DASHBOARD (Epic 4) — Projeto/Área/Pescador/Colônia,
          combináveis entre si. Permissão do Epic 1 já vem aplicada pelos
          endpoints de opções (projetos/areas/pescadores) e reforçada de novo
          no /pesca/stats no backend. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        <FiltroProjetosMulti
          projetos={projetos}
          selecionados={filtros.uid_pesca_projetos}
          onChange={(uids) => setFiltros(prev => ({ ...prev, uid_pesca_projetos: uids }))}
        />

        <FiltroLocalArvore
          nos={arvoreLocais}
          selecionados={filtros.uid_pesca_local}
          onChange={(uids) => setFiltros(prev => ({ ...prev, uid_pesca_local: uids }))}
        />

        <button
          type="button"
          onClick={() => setMaisFiltrosAberto((a) => !a)}
          style={{ ...filterSelectStyle, cursor: 'pointer', flex: '0 0 auto' }}
          title="Área do pescador e colônia"
        >
          Mais filtros {maisFiltrosAberto ? '▴' : '▾'}{!maisFiltrosAberto && (filtros.uid_pesca_area || filtros.uid_pesca_colonia || filtros.validacao === 'somente_validados') ? ' •' : ''}
        </button>

        {maisFiltrosAberto && (
          <>
            <select
              value={filtros.uid_pesca_area || ''}
              onChange={(e) => setFiltros(prev => ({ ...prev, uid_pesca_area: e.target.value || null }))}
              style={{ ...filterSelectStyle, width: '190px', flex: '0 0 190px', textOverflow: 'ellipsis' }}
            >
              <option value="">Todas as áreas do pescador</option>
              {areas.map((a) => (
                <option key={a.uid_pesca_area} value={a.uid_pesca_area}>{a.nome}</option>
              ))}
            </select>

            <select
              value={filtros.uid_pesca_colonia || ''}
              onChange={(e) => setFiltros(prev => ({ ...prev, uid_pesca_colonia: e.target.value || null }))}
              style={{ ...filterSelectStyle, width: '190px', flex: '0 0 190px', textOverflow: 'ellipsis' }}
            >
              <option value="">Todas as colônias</option>
              {colonias.map((c) => (
                <option key={c.uid_pesca_colonia} value={c.uid_pesca_colonia}>{c.nome}</option>
              ))}
            </select>

            <label
              title="Mostra só os registros que já passaram pela validação (têm data de validação). Padrão: todos os registros."
              style={{ ...filterSelectStyle, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flex: '0 0 auto' }}
            >
              <input
                type="checkbox"
                checked={filtros.validacao === 'somente_validados'}
                onChange={(e) => setFiltros(prev => ({ ...prev, validacao: e.target.checked ? 'somente_validados' : 'todos' }))}
              />
              Somente validados
            </label>
          </>
        )}

        {/* PERÍODO: na linha de filtros, junto com Projeto/Local/Mais filtros */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flex: '0 0 auto' }}>
          <select
            value={periodoSelecionado}
            onChange={(e) => handleSelectPeriodo(e.target.value)}
            title="Período de registro"
            style={{ ...filterSelectStyle, width: '150px' }}
          >
            {['Hoje', 'Ontem', 'Essa semana', 'Semana passada', 'Esse Mes', 'Mes passado', 'Esse ano'].map(op => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>
          <input
            type="date"
            value={dataInicioDigitada}
            min={`${ANO_MINIMO}-01-01`}
            max={`${ANO_MAXIMO}-12-31`}
            onChange={e => setDataInicioDigitada(e.target.value)}
            aria-label="Data inicial"
            style={{ ...filterSelectStyle, width: '140px' }}
          />
          <input
            type="date"
            value={dataFimDigitada}
            min={`${ANO_MINIMO}-01-01`}
            max={`${ANO_MAXIMO}-12-31`}
            onChange={e => setDataFimDigitada(e.target.value)}
            aria-label="Data final"
            style={{ ...filterSelectStyle, width: '140px' }}
          />
        </div>

        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '200px' }}>
          <input
            type="text"
            value={pescadorBusca}
            onChange={(e) => handleBuscarPescador(e.target.value)}
            placeholder="Buscar pescador..."
            autoComplete="off"
            style={{ ...filterSelectStyle, width: '100%', boxSizing: 'border-box' }}
          />
          {pescadorSelecionado && (
            <button
              onClick={limparPescador}
              title="Limpar pescador"
              style={{ position: 'absolute', right: '6px', top: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#999' }}
            >
              ×
            </button>
          )}
          {(buscandoPescador || sugestoesPescador.length > 0) && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #ddd', borderRadius: '8px', marginTop: '2px', zIndex: 10,
              maxHeight: '180px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}>
              {buscandoPescador && <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Buscando...</div>}
              {!buscandoPescador && sugestoesPescador.map((p) => (
                <div
                  key={p.uid_pesca_pescador}
                  onClick={() => selecionarPescador(p)}
                  style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0' }}
                >
                  {p.nome}
                </div>
              ))}
              {!buscandoPescador && sugestoesPescador.length === 0 && pescadorBusca.trim().length >= 2 && (
                <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Nenhum pescador encontrado</div>
              )}
            </div>
          )}
        </div>

      </div>

      {avisoPeriodo && (
        <p role="status" style={{ margin: '-8px 0 10px', fontSize: '0.8rem', color: '#a15c00' }}>
          {avisoPeriodo}
        </p>
      )}

      {/* Falha ao aplicar o último filtro nos cards e na produção por espécie: os números abaixo são da busca anterior */}
      <AvisoDadosDesatualizados erro={!!avisoBusca} comDadosAnteriores={!!stats} detalhe={avisoBusca} carregando={loading} onTentarDeNovo={() => setTentativaStats((t) => t + 1)} />

      {qtdFiltrosSeletorAtivos > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <span style={{
            background: '#f75a68', color: '#fff', borderRadius: '12px',
            padding: '2px 10px', fontSize: '0.75rem', fontWeight: 'bold',
          }}>
            {qtdFiltrosSeletorAtivos} filtro{qtdFiltrosSeletorAtivos > 1 ? 's' : ''} ativo{qtdFiltrosSeletorAtivos > 1 ? 's' : ''}
          </span>
          {filtros.validacao === 'somente_validados' && (
            <span style={{
              marginLeft: '8px', background: '#eaf8f1', color: '#0a7a4f', border: '1px solid #b6e6cf', borderRadius: '12px',
              padding: '2px 4px 2px 10px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px',
            }}>
              Somente validados
              <button type="button" title="Voltar a mostrar todos" onClick={() => setFiltros(prev => ({ ...prev, validacao: 'todos' }))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#0a7a4f', fontWeight: 'bold', padding: '0 6px' }}>✕</button>
            </span>
          )}
          {filtros.uid_pesca_apetrecho && (
            <span style={{
              marginLeft: '8px', background: '#fff4e5', color: '#9a5b00', border: '1px solid #ffd9a0', borderRadius: '12px',
              padding: '2px 4px 2px 10px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px',
            }}>
              Petrecho: {nomePetrecho || '…'}
              <button type="button" title="Limpar petrecho" onClick={() => selecionarPetrecho(null, null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9a5b00', fontWeight: 'bold', padding: '0 6px' }}>✕</button>
            </span>
          )}
          {filtros.uid_pesca_local.length > 0 && (
            <span style={{
              marginLeft: '8px', background: '#eef9ff', color: '#0a6c8f', border: '1px solid #bfe9f9', borderRadius: '12px',
              padding: '2px 4px 2px 10px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px',
            }}>
              {modoComparacaoLocal
                ? `Locais: ${filtros.uid_pesca_local.length} selecionados`
                : `Local: ${caminhoLocal.length ? caminhoLocal[caminhoLocal.length - 1].nome : (arvoreLocais.find((n) => n.uid_pesca_local === filtros.uid_pesca_local[0])?.nome || '…')}`}
              <button type="button" title="Voltar para Todos" onClick={() => irParaLocal(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#0a6c8f', fontWeight: 'bold', padding: '0 6px' }}>✕</button>
            </span>
          )}
        </div>
      )}

      {/* 4 CARDS PRINCIPAIS (grid de 4 colunas iguais, mesma largura dos gráficos) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '20px', marginBottom: '10px', opacity: loading ? 0.6 : 1 }}>
        <StatCard icon={<People />} label="Informantes" value={formatarNumero(stats.totalPescadores, 0)}
          variacao={montarVariacao(stats.totalPescadores, stats.anterior, 'totalPescadores', (v) => formatarNumero(v, 0))}
          tooltip="Informantes: pescadores cadastrados que registraram pescarias. Pescadores envolvidos: soma do tamanho de grupo mais comum de cada informante no período. Estimativa: companheiros não são identificados e podem ser contados duas vezes se pescarem com mais de um informante."
          secundario={stats.pescadoresEnvolvidos != null && (
            <MuiTooltip title="Informantes: pescadores cadastrados que registraram pescarias. Pescadores envolvidos: soma do tamanho de grupo mais comum de cada informante no período. Estimativa: companheiros não são identificados e podem ser contados duas vezes se pescarem com mais de um informante." arrow>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>≈ {formatarNumero(stats.pescadoresEnvolvidos, 0)} pescadores envolvidos (estimativa)</span>
            </MuiTooltip>
          )} />
        <StatCard icon={<Scale />} label="Total (kg)" value={formatarKg(stats.pesoTotal)} color="#00b37e"
          variacao={montarVariacao(stats.pesoTotal, stats.anterior, 'pesoTotal', (v) => `${formatarKg(v)} kg`)}
          tooltip={`Peso padronizado para equivalente a peixe inteiro (eviscerado ×1,2, filé ×3). Peso informado: ${formatarKg(Number(stats.pesoInformado) || 0)} kg.`} />
        <StatCard icon={<Phishing />} label="Registros" value={formatarNumero(stats.totalLancamentos, 0)}
          variacao={montarVariacao(stats.totalLancamentos, stats.anterior, 'totalLancamentos', (v) => formatarNumero(v, 0))} />
        <StatCard icon={<Speed />} label="Rendimento" value={stats.registrosCpue > 0 ? formatarRendimento(stats.cpue) : 'n/d'} unidade="kg/pescador-dia" color="#00c3ff"
          variacao={stats.registrosCpue > 0 ? montarVariacao(stats.cpue, stats.anterior, 'cpue', (v) => `${formatarRendimento(v)} kg/pescador-dia`) : undefined}
          tooltip={`Soma do peso convertido ÷ soma de pescadores por saída. Base: ${formatarNumero(stats.registrosCpue, 0)} de ${formatarNumero(stats.totalLancamentos, 0)} registros (exclui registros sem nº de pescadores).`}
          secundario={
            <MuiTooltip title={`Base: ${formatarNumero(stats.registrosCpueBarco, 0)} registros com embarcação informada`} arrow>
              <span>{stats.cpueBarco != null && stats.registrosCpueBarco > 0 ? `${formatarRendimento(stats.cpueBarco)} kg/barco-dia` : 'n/d kg/barco-dia'}</span>
            </MuiTooltip>
          } />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '10px' }}>

        {/* PRODUÇÃO POR ESPÉCIE (COM FILTRO AO CLICAR) */}
        <div className={styles.sectionCard} style={{ minHeight: '500px' }}>
          <div className={styles.sectionHeader}>
            Produção por Espécie {filtros.uid_pesca_especie && <small>(Filtrado por: {filtros.nome_especie || '…'})</small>}
          </div>
          <CartaoExpansivel titulo="Produção por Espécie" alturaNormal={430}>
            {(altura) => {
              // A altura numérica é a do cartão normal; a do modal de tela cheia vem como string ('75vh').
              const expandido = typeof altura === 'string';
              const dadosEspecies = montarDadosEspecies(!expandido);
              return (
              <div style={{ width: '100%', height: expandido ? `max(${altura}, ${dadosEspecies.length * 28 + 40}px)` : altura, padding: '10px 20px 20px 10px', boxSizing: 'border-box' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={dadosEspecies} margin={{ left: 40, right: 120 }} onClick={(data) => {
                    const alvo = data && dadosEspecies.find((d: any) => d.label === data.activeLabel);
                    if (alvo && !alvo.agrupada && alvo.uid) selecionarEspecie(alvo.uid, alvo.label);
                  }}>
                    <XAxis type="number" hide />
                    <YAxis dataKey="label" type="category" width={140} axisLine={false} tickLine={false} style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }} />
                    <Tooltip cursor={{ fill: '#f1f4f6' }} formatter={(value: any) => [`${formatarKg(value)} kg`, 'Peso Total']} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18} style={{ cursor: 'pointer' }}>
                      {dadosEspecies.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.agrupada ? '#92989b' : entry.uid && entry.uid === filtros.uid_pesca_especie ? '#f75a68' : (index < 3 ? '#00b37e' : '#00c3ff')} />
                      ))}
                      <LabelList dataKey="rotuloBarra" position="right" style={{ fontSize: '11px', fill: '#555' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              );
            }}
          </CartaoExpansivel>
        </div>

        {/* PRODUÇÃO POR LOCAL (navegação hierárquica; filtra o Dashboard) */}
        <div className={styles.sectionCard} style={{ minHeight: '500px' }}>
          <div className={styles.sectionHeader}>Produção por local</div>

          {/* BREADCRUMB: Todos > nó > nó — cada trecho volta pra aquele nível (e filtra o Dashboard por ele) */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', padding: '0 20px 6px', fontSize: '0.85rem', color: '#666' }}>
            {filtros.uid_pesca_local.length === 0 ? (
              <strong style={{ color: '#333' }}>Todos</strong>
            ) : (
              <button type="button" onClick={() => irParaLocal(null)} style={breadcrumbBtnStyle}>Todos</button>
            )}
            {modoComparacaoLocal && (
              <>
                <span style={{ color: '#aaa' }}>&gt;</span>
                <strong style={{ color: '#333' }}>{filtros.uid_pesca_local.length} locais selecionados (comparação)</strong>
              </>
            )}
            {caminhoLocal.map((no, idx) => (
              <React.Fragment key={no.uid_pesca_local}>
                <span style={{ color: '#aaa' }}>&gt;</span>
                {idx === caminhoLocal.length - 1 ? (
                  <strong style={{ color: '#333' }}>{no.nome}</strong>
                ) : (
                  <button type="button" onClick={() => irParaLocal(no.uid_pesca_local)} style={breadcrumbBtnStyle}>{no.nome}</button>
                )}
              </React.Fragment>
            ))}
          </div>

          <div style={{ padding: '0 20px' }}>
            <AvisoDadosDesatualizados erro={!!erroLocal} comDadosAnteriores={!!nivelLocal} detalhe={erroLocal} carregando={carregandoLocal} onTentarDeNovo={() => setTentativaLocal((t) => t + 1)} />
          </div>

          <CartaoExpansivel titulo="Produção por local" alturaNormal={430}>
            {(altura) => {
              // A altura numérica é a do cartão normal; a do modal de tela cheia vem como string ('75vh').
              const expandido = typeof altura === 'string';
              const dadosLocais = montarDadosLocais(!expandido);
              if (!carregandoLocal && dadosLocais.length === 0) {
                // com erro e sem dado anterior, o aviso acima já explica (e oferece tentar de novo)
                return erroLocal
                  ? <div style={{ padding: '20px' }} />
                  : <p style={{ padding: '20px', color: '#999' }}>Nenhuma produção para os filtros atuais.</p>;
              }
              return (
                <div style={{ width: '100%', height: expandido ? `max(${altura}, ${dadosLocais.length * 28 + 40}px)` : altura, padding: '10px 20px 20px 10px', boxSizing: 'border-box', opacity: carregandoLocal ? 0.6 : 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={dadosLocais} margin={{ left: 0, right: 150 }} onClick={(data) => {
                      const alvo = data && dadosLocais.find((d: any) => d.label === data.activeLabel);
                      if (alvo && alvo.categoria === 'no' && alvo.temFilhos) {
                        irParaLocal(alvo.uid);
                      } else if (alvo && alvo.categoria === 'sem_vinculo') {
                        // Abre Registros já filtrado (só projeto e período viajam).
                        navigate(montarUrlRegistrosSemLocal(filtros));
                      }
                    }}>
                      <XAxis type="number" hide />
                      <YAxis dataKey="label" type="category" width={190} axisLine={false} tickLine={false} tick={<RotuloEixoLocal />} interval={0} />
                      <Tooltip cursor={{ fill: '#f1f4f6' }} formatter={(value: any) => [`${formatarKg(value)} kg`, 'Peso Total']} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                        {dadosLocais.map((entry: any, index: number) => (
                          <Cell
                            key={`local-${index}`}
                            fill={entry.agrupada || entry.categoria !== 'no' ? '#92989b' : (index < 3 ? '#00b37e' : '#00c3ff')}
                            style={{ cursor: (entry.categoria === 'no' && entry.temFilhos) || entry.categoria === 'sem_vinculo' ? 'pointer' : 'default' }}
                          />
                        ))}
                        <LabelList dataKey="rotuloBarra" position="right" style={{ fontSize: '11px', fill: '#555' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            }}
          </CartaoExpansivel>
        </div>
      </div>

      {/* EVOLUÇÃO DA PRODUÇÃO (reage aos filtros globais; agregação e métrica são internas ao gráfico) */}
      <EvolucaoProducao
        filtrosQuery={montarQueryFiltros(filtros).toString()}
        mostrarPiracema={devePiracema({
          locaisSelecionados: filtros.uid_pesca_local,
          arvore: arvoreLocais,
          // sem filtro de local, o gráfico "Produção por local" está no nível raiz e mostra de quais bacias vem a produção
          itensNivelRaiz: filtros.uid_pesca_local.length === 0 && nivelLocal ? nivelLocal.itens : null,
        })}
      />

      {/* MAPA DE PRODUÇÃO (entre a Evolução da produção e o Esforço de pesca) — agregado por local
          canônico + projeto, com cross-filter: os demais widgets já reagem a uid_pesca_local
          porque todos leem do mesmo `filtros` compartilhado; só a direção
          "mapa -> filtro" (o clique) é código novo, feito aqui. */}
      <MapaProducao filtrosBase={filtros} tituloLocal={tituloLocalMapa} onNavegar={irParaLocal} />

      {/* ESFORÇO DE PESCA: petrechos (clique filtra o Dashboard), tripulação e embarcação */}
      <EsforcoPesca
        filtrosQuery={montarQueryFiltros(filtros).toString()}
        petrechoSelecionado={filtros.uid_pesca_apetrecho}
        onSelecionarPetrecho={selecionarPetrecho}
      />

      {/* ATIVIDADE INTERROMPIDA: períodos sem atividade (projeto, período, pescador, colônia e área; espécie, petrecho e local não se aplicam) */}
      <AtividadeInterrompida filtrosQuery={montarQueryFiltros(filtros).toString()} />

      {/* QUALIDADE DOS DADOS: quanto de cada informação está preenchida no recorte atual (fim da página) */}
      <QualidadeDados filtrosQuery={montarQueryFiltros(filtros).toString()} />

      {/* Comparativo por projeto/área (DashboardComparativo + /pesca/stats/comparativo) fica fora da
          página principal por ora — componente e endpoint mantidos pra uso futuro. */}
    </div>
  );
};

// Variação % do indicador contra o período anterior de mesmo tamanho (stats.anterior, calculado no backend).
interface VariacaoCard {
  pct: number | null; // null = sem base (sem período filtrado ou período anterior sem dado): não mostra nada
  tooltip: string;
}

const dataBRDeISO = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const montarVariacao = (
  atual: number,
  anterior: any,
  campo: 'totalPescadores' | 'pesoTotal' | 'totalLancamentos' | 'cpue',
  formatar: (v: number) => string
): VariacaoCard => {
  if (!anterior) return { pct: null, tooltip: '' };
  const valorAnterior = Number(anterior[campo]) || 0;
  const intervalo = `${dataBRDeISO(anterior.periodo.inicio)} a ${dataBRDeISO(anterior.periodo.fim)}`;
  // Com dias futuros no filtro, o backend compara só o decorrido (até hoje) com os mesmos dias do período anterior.
  const comparado = anterior.atual ? ` — comparado com ${dataBRDeISO(anterior.atual.inicio)} a ${dataBRDeISO(anterior.atual.fim)}${anterior.parcial ? ' (até hoje)' : ''}` : '';
  const tooltip = `Período anterior: ${formatar(valorAnterior)} (${intervalo})${comparado}`;
  if (!(valorAnterior > 0)) return { pct: null, tooltip };
  return { pct: ((Number(atual) - valorAnterior) / valorAnterior) * 100, tooltip };
};

const VariacaoBadge = ({ variacao }: { variacao: VariacaoCard }) => {
  const { pct } = variacao;
  if (pct == null) return null; // sem período anterior com dados: nada no lugar da variação
  const subiu = pct > 0.05;
  const caiu = pct < -0.05;
  const cor = subiu ? '#00b37e' : caiu ? '#f75a68' : '#999';
  const texto = `${subiu ? '▲' : caiu ? '▼' : '■'} ${formatarNumero(Math.abs(pct), 1)}%`;
  return (
    <MuiTooltip title={variacao.tooltip} arrow>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: cor, whiteSpace: 'nowrap', cursor: 'default' }}>{texto}</span>
    </MuiTooltip>
  );
};

// COMPONENTE DE CARD — mesma estrutura nos 4: rótulo / valor (+ unidade) / linha secundária.
const StatCard = ({ icon, label, value, unidade, color = '#666', tooltip, secundario, variacao }: any) => {
  const card = (
  <div style={{ background: '#fff', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 20px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid #f0f0f0', height: '104px', boxSizing: 'border-box', minWidth: 0 }}>
    <div style={{ background: '#eef9ff', padding: '12px', borderRadius: '10px', color: '#00c3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{icon}</div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{ margin: 0, fontSize: '0.85rem', color: '#888', fontWeight: '600', whiteSpace: 'nowrap' }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', whiteSpace: 'nowrap' }}>
        <h2 style={{ margin: 0, color: color, fontSize: '1.4rem', fontWeight: 'bold', lineHeight: 1.25 }}>{value}</h2>
        {unidade && <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#888' }}>{unidade}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '1.1rem', fontSize: '0.75rem', color: '#888', whiteSpace: 'nowrap' }}>
        {variacao && <VariacaoBadge variacao={variacao} />}
        {secundario}
      </div>
    </div>
  </div>
  );
  return tooltip ? <MuiTooltip title={tooltip} arrow>{card}</MuiTooltip> : card;
};

const breadcrumbBtnStyle: React.CSSProperties = {
  border: 'none', background: 'transparent', color: '#0a6c8f', cursor: 'pointer', padding: 0, fontSize: '0.85rem', textDecoration: 'underline',
};

const filterSelectStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff',
};

export default Dashboard;